import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsRight } from 'lucide-react';
// Fase 5.17 -- `useCurrentUser` es un selector zustand a nivel de módulo, NO React context: el
// motivo de la regla de frontera (acoplar re-renders / dificultar el test) no aplica -- misma
// excepción documentada que `DocsCollaborativeEditor` (5.10) / `SetSettings` (5.16). `toast`
// sí llega por prop (`taskWorkframeSlots` lo threadea desde `ComponentTaskHoy`).
// eslint-disable-next-line no-restricted-imports
import { useCurrentUser } from '../../../../context/authHooks';
import { useWorkframe } from '../../hooks/useWorkframes';
import { useWorkframePref } from '../../hooks/useWorkframePrefs';
import WorkframeIndexColumn from './WorkframeIndexColumn';
import WorkframeDocColumn from './WorkframeDocColumn';
import WorkframeBitacoraColumn from './WorkframeBitacoraColumn';
import './TaskWorkframePanel.css';

// Panel de Cuadro de Trabajo (integración main→dev, epic D1.3). Orquestador de las 3 columnas
// (Índice fijo a la izquierda + Documentación + Bitácora en un canvas con scroll horizontal) y
// envuelve el panel de tareas + comentarios como `children`. Cuando no hay `workframeId`
// devuelve `children` sin tocar — así los contextos standalone/público/overlay no cargan las
// columnas.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: hidrata la preferencia de colapso guardada una vez cargada, y sincroniza el doc seleccionado con el workframe/tarea activos.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const coerceBool = (v) => v === true || v === 'true' || v === 1 || v === '1';

export default function TaskWorkframePanel({
    workframeId,
    spaceId,
    activeTaskId,
    activeTask,
    onSelectTask,
    onCreateTask,
    onAddSuggestion,
    children,
    toast,
}) {
    const { t } = useTranslation('tasks');
    const { dbId } = useCurrentUser();

    const { data: workframe } = useWorkframe(workframeId);

    const [selectedDocId, setSelectedDocId] = useState(null);
    const [bitacoraDocId, setBitacoraDocId] = useState(null);
    const [indexWidth, setIndexWidth] = useState(260);

    const collapsePref = useWorkframePref(dbId, 'workframe_doc_collapsed', { enabled: !!dbId, coerce: coerceBool });
    const [isDocCollapsed, setIsDocCollapsed] = useState(false);
    const collapseHydratedRef = useRef(false);

    const shouldAutoSelectLatestRef = useRef(!activeTaskId);

    useEffect(() => {
        if (collapseHydratedRef.current) return;
        if (collapsePref.isLoaded) {
            collapseHydratedRef.current = true;
            if (collapsePref.value != null) setIsDocCollapsed(collapsePref.value);
        }
    }, [collapsePref.isLoaded, collapsePref.value]);

    // Semilla de documento seleccionado cuando el cuadro de trabajo carga.
    useEffect(() => {
        if (!workframe) {
            setSelectedDocId(null);
            setBitacoraDocId(null);
            return;
        }
        const initialDoc = activeTask?.doc_id || workframe.doc_id;
        setSelectedDocId((prev) => prev || initialDoc);
        setBitacoraDocId((prev) => prev || workframe.doc_id);
    }, [workframe, activeTask?.doc_id]);

    // La tarea activa manda su propio `doc_id`.
    useEffect(() => {
        if (activeTask?.doc_id) setSelectedDocId(activeTask.doc_id);
    }, [activeTask?.id, activeTask?.doc_id]);

    const handleToggleDocCollapse = useCallback(() => {
        setIsDocCollapsed((prev) => {
            const next = !prev;
            collapsePref.save(next ? 'true' : 'false');
            toast.success(next ? t('workframe.doc.collapsed') : t('workframe.doc.expanded'));
            return next;
        });
    }, [collapsePref, toast, t]);

    const handleSelectDocFromIndex = useCallback((docId) => {
        setSelectedDocId(docId);
        setBitacoraDocId(docId);
    }, []);

    const handleBitacoraTasksLoaded = useCallback((loadedTasks) => {
        if (!shouldAutoSelectLatestRef.current) return;
        shouldAutoSelectLatestRef.current = false;
        if (Array.isArray(loadedTasks) && loadedTasks.length > 0) {
            const latest = loadedTasks[0];
            if (onSelectTask && String(latest.id) !== String(activeTaskId)) {
                onSelectTask(latest.id, latest);
            }
        }
    }, [onSelectTask, activeTaskId]);

    const handleSelectTaskFromBitacora = useCallback((taskId, taskObj) => {
        shouldAutoSelectLatestRef.current = false;
        const targetDocId = taskObj?.doc_id || workframe?.doc_id;
        if (targetDocId) setSelectedDocId(targetDocId);
        onSelectTask?.(taskId, taskObj);
    }, [workframe?.doc_id, onSelectTask]);

    const handleAddSuggestionFromBitacora = useCallback((taskId, taskObj) => {
        shouldAutoSelectLatestRef.current = false;
        const targetDocId = taskObj?.doc_id || workframe?.doc_id;
        if (targetDocId) setSelectedDocId(targetDocId);
        onAddSuggestion?.(taskId, taskObj);
    }, [workframe?.doc_id, onAddSuggestion]);

    if (!workframeId) return children || null;

    const rootDocId = workframe?.doc_id || null;
    const currentSelectedDocId = selectedDocId || activeTask?.doc_id || workframe?.doc_id || null;
    const effectiveSpaceId = workframe?.space_id || spaceId || activeTask?.space_id;

    return (
        <>
            <WorkframeIndexColumn
                key={`index-${workframeId}`}
                spaceId={effectiveSpaceId}
                rootDocId={rootDocId}
                selectedDocId={currentSelectedDocId}
                onSelectDoc={handleSelectDocFromIndex}
                toast={toast}
                onDocCreated={(newDoc) => {
                    setSelectedDocId(newDoc.id);
                    setBitacoraDocId(newDoc.id);
                }}
                onWidthChange={setIndexWidth}
            />

            {isDocCollapsed && (
                <button
                    type="button"
                    className="wf-doc-collapse-floating-btn"
                    style={{ left: `${indexWidth}px` }}
                    onClick={handleToggleDocCollapse}
                    title={t('workframe.doc.expand')}
                >
                    <ChevronsRight size={14} />
                </button>
            )}

            <div className="wf-scroll-canvas">
                {workframe && (
                    <>
                        <WorkframeDocColumn
                            docId={currentSelectedDocId}
                            spaceId={workframe.space_id || spaceId}
                            isCollapsed={isDocCollapsed}
                            onToggleCollapse={handleToggleDocCollapse}
                            toast={toast}
                        />

                        <WorkframeBitacoraColumn
                            workframeId={workframe.id}
                            selectedDocId={bitacoraDocId || workframe.doc_id}
                            activeTaskId={activeTask?.id || activeTaskId}
                            activeTask={activeTask}
                            onSelectTask={handleSelectTaskFromBitacora}
                            onCreateTask={(docId) => onCreateTask?.(docId || currentSelectedDocId || bitacoraDocId || workframe.doc_id)}
                            onAddSuggestion={handleAddSuggestionFromBitacora}
                            onTasksLoaded={handleBitacoraTasksLoaded}
                        />
                    </>
                )}
                {children}
            </div>
        </>
    );
}
